import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
    getExpedientesAnses, agregarExpedienteAnses, editarExpedienteAnses, eliminarExpedienteAnses, seguirExpedienteAnses,
} from '../services/tramiteService';
import { formatoExpediente } from '../pages/Listados/paraAprobar';

const VACIO = { nroExpediente: '', fechaIngreso: '', descripcion: '' };

/**
 * Dónde va la tarjeta en el detalle: arriba solo mientras el trámite está en "Seguimiento de
 * Expediente" (es lo que se está mirando); en los demás estados, debajo de los datos del trámite y
 * del cliente.
 */
export function enSeguimientoDeExpediente(nombreEstado) {
    return String(nombreEstado || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase().includes('seguimiento de expediente');
}

const fecha = (iso) => {
    if (!iso) return null;
    const [a, m, d] = String(iso).slice(0, 10).split('-');
    return a && m && d ? `${d}/${m}/${a}` : iso;
};

/**
 * "Expedientes en ANSES" del detalle de un trámite (V265). Un trámite puede tener varios (el
 * principal, un reclamo, una apelación…). Sirven para que Para aprobar y los avisos del seguimiento
 * sepan qué expediente de Mi ANSES es de este trámite; el robot lee igual todos los del cliente.
 * Los que quedaron en el resultado de un turno ANSES se ven acá y se cambian desde el turno.
 * Se siguen todos; destildar "Seguir" saca a ese expediente de los avisos y de Para aprobar (es la
 * misma marca que en Seguimiento de Expedientes).
 */
export default function ExpedientesAnsesTramite({ idTramite, soloLectura = false }) {
    const [expedientes, setExpedientes] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [form, setForm] = useState(null); // null = cerrado; { id?, ...campos }
    const [guardando, setGuardando] = useState(false);

    const cargar = useCallback(async () => {
        if (!idTramite) return;
        try {
            setExpedientes(await getExpedientesAnses(idTramite));
        } catch {
            setExpedientes([]);
        } finally {
            setCargando(false);
        }
    }, [idTramite]);

    useEffect(() => { cargar(); }, [cargar]);

    const guardar = async (e) => {
        e.preventDefault();
        if (!form.nroExpediente.trim()) {
            toast.error('Falta el número de expediente.');
            return;
        }
        setGuardando(true);
        const datos = {
            nroExpediente: form.nroExpediente.trim(),
            fechaIngreso: form.fechaIngreso || null,
            descripcion: form.descripcion.trim() || null,
        };
        try {
            const lista = form.id
                ? await editarExpedienteAnses(idTramite, form.id, datos)
                : await agregarExpedienteAnses(idTramite, datos);
            setExpedientes(lista);
            setForm(null);
            toast.success(form.id ? 'Expediente actualizado' : 'Expediente agregado');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'No se pudo guardar el expediente');
        } finally {
            setGuardando(false);
        }
    };

    const cambiarSeguir = async (exp, seguir) => {
        setExpedientes((prev) => prev.map((e) => (e.nroExpediente === exp.nroExpediente ? { ...e, seguido: seguir } : e)));
        try {
            setExpedientes(await seguirExpedienteAnses(idTramite, exp.nroExpediente, seguir));
        } catch (err) {
            toast.error(err?.response?.data?.message || 'No se pudo cambiar el seguimiento del expediente');
            cargar();
        }
    };

    const eliminar = async (exp) => {
        if (!window.confirm(`¿Quitar el expediente ${formatoExpediente(exp.nroExpediente)} de este trámite?`)) return;
        try {
            setExpedientes(await eliminarExpedienteAnses(idTramite, exp.id));
            toast.success('Expediente quitado');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'No se pudo quitar el expediente');
        }
    };

    // Misma tarjeta que el resto del detalle (fila con separación arriba y abajo, sombra, bordes
    // redondeados y título azul): antes quedaba pegada a la de arriba y sin aire.
    return (
        <div className="row g-4 mt-1 mb-4">
          <div className="col-12">
            <div className="card shadow-lg border-0 rounded-4 p-4">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <h4 className="fw-bold text-primary mb-0">
                        <i className="bi bi-folder2-open me-2" />Expedientes en ANSES
                    </h4>
                    {!soloLectura && !form && (
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setForm({ ...VACIO })}>
                            <i className="bi bi-plus-lg me-1" />Agregar expediente
                        </button>
                    )}
                </div>

                {cargando ? (
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>Cargando…</div>
                ) : expedientes.length === 0 && !form ? (
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                        Sin expedientes cargados. Si el trámite ya se presentó en ANSES, cargá el número para que Para aprobar
                        y los avisos del seguimiento lo reconozcan.
                    </div>
                ) : (
                    <ul className="list-unstyled mb-0">
                        {expedientes.map((exp) => (
                            <li key={exp.id ?? `turno-${exp.nroExpediente}`} className="d-flex flex-wrap align-items-center gap-2 py-1"
                                style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <label className="form-check mb-0 d-flex align-items-center gap-1" style={{ fontSize: '0.78rem', color: '#4b5563' }}
                                    title="Si no se sigue, no avisa ni cuenta en Para aprobar">
                                    <input type="checkbox" className="form-check-input mt-0" checked={exp.seguido !== false}
                                        disabled={soloLectura} aria-label={`Seguir ${exp.nroExpediente}`}
                                        onChange={(e) => cambiarSeguir(exp, e.target.checked)} />
                                    Seguir
                                </label>
                                <span style={{ fontFamily: 'monospace', fontSize: '0.88rem', color: exp.seguido === false ? '#9ca3af' : '#1e293b',
                                    textDecoration: exp.seguido === false ? 'line-through' : 'none' }}>{formatoExpediente(exp.nroExpediente)}</span>
                                {exp.principal && (
                                    <span className="badge" style={{ background: '#dbeafe', color: '#1e40af' }} title="El que usa Para aprobar">Principal</span>
                                )}
                                {exp.origen === 'TURNO' && (
                                    <span className="badge" style={{ background: '#f1f5f9', color: '#475569' }} title="Se cambia desde la tarjeta del turno ANSES">Del turno ANSES</span>
                                )}
                                {exp.descripcion && exp.origen !== 'TURNO' && <span style={{ fontSize: '0.82rem', color: '#4b5563' }}>· {exp.descripcion}</span>}
                                {exp.fechaIngreso && <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>· ingresó {fecha(exp.fechaIngreso)}</span>}
                                {!soloLectura && exp.origen === 'CARGADO' && (
                                    <span className="ms-auto d-flex gap-1">
                                        <button type="button" className="btn btn-sm btn-link p-0" aria-label={`Editar ${exp.nroExpediente}`}
                                            onClick={() => setForm({ id: exp.id, nroExpediente: exp.nroExpediente, fechaIngreso: exp.fechaIngreso || '', descripcion: exp.descripcion || '' })}>
                                            <i className="bi bi-pencil" />
                                        </button>
                                        <button type="button" className="btn btn-sm btn-link p-0 text-danger" aria-label={`Quitar ${exp.nroExpediente}`}
                                            onClick={() => eliminar(exp)}>
                                            <i className="bi bi-trash" />
                                        </button>
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}

                {form && (
                    <form className="mt-2 p-2" style={{ background: '#f8fafc', borderRadius: 8 }} onSubmit={guardar}>
                        <div className="row g-2">
                            <div className="col-md-5">
                                <label className="form-label mb-0" htmlFor="exp-anses-nro" style={{ fontSize: '0.8rem' }}>N° de expediente</label>
                                <input id="exp-anses-nro" className="form-control form-control-sm" maxLength={60} autoFocus
                                    placeholder="024-20-12345678-9-007-01"
                                    value={form.nroExpediente} onChange={(e) => setForm({ ...form, nroExpediente: e.target.value })} />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label mb-0" htmlFor="exp-anses-fecha" style={{ fontSize: '0.8rem' }}>Fecha de ingreso</label>
                                <input id="exp-anses-fecha" type="date" className="form-control form-control-sm"
                                    value={form.fechaIngreso} onChange={(e) => setForm({ ...form, fechaIngreso: e.target.value })} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label mb-0" htmlFor="exp-anses-desc" style={{ fontSize: '0.8rem' }}>Descripción <span className="text-muted">(opcional)</span></label>
                                <input id="exp-anses-desc" className="form-control form-control-sm" maxLength={150} placeholder="Reclamo, apelación…"
                                    value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
                            </div>
                        </div>
                        <div className="d-flex justify-content-end gap-2 mt-2">
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setForm(null)}>Cancelar</button>
                            <button type="submit" className="btn btn-sm btn-primary" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</button>
                        </div>
                    </form>
                )}
            </div>
          </div>
        </div>
    );
}
